import { apiFetch } from './client';

export type UserType = 'internal' | 'client';

export interface KeycloakUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  attributes?: Record<string, string[]>;
  createdTimestamp?: number;
  groups?: UserGroupMembership[];
}

export interface KeycloakGroup {
  id: string;
  name: string;
  path: string;
  description?: string;
  members?: GroupMember[];
}

export interface GroupMember {
  userId: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  userType: UserType;
  enabled: boolean;
}

export interface UserGroupMembership {
  groupId: string;
  groupName: string;
}

export interface ListUsersResponse {
  users: KeycloakUser[];
  total: number;
}

export interface CreateUserPayload {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  temporary?: boolean;
  userType: UserType;
}

export interface UpdateUserPayload {
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  userType?: UserType;
}

export interface InviteExternalUserPayload {
  email: string;
  message?: string;
  groups?: string[];
}

export interface B2BInvitationResult {
  invitationId: string;
  invitedUserEmailAddress: string;
  inviteRedeemUrl?: string;
  status?: string;
  invitedUserId?: string;
}

export const keycloakApi = {
  listUsers(params?: { search?: string; first?: number; max?: number }) {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.first !== undefined) query.set('first', String(params.first));
    if (params?.max !== undefined) query.set('max', String(params.max));
    const qs = query.toString();
    return apiFetch<ListUsersResponse>(`/admin/kc/users${qs ? `?${qs}` : ''}`);
  },

  createUser(data: CreateUserPayload) {
    return apiFetch<KeycloakUser>('/admin/kc/users', {
      method: 'POST',
      data,
    });
  },

  inviteExternalUser(data: InviteExternalUserPayload) {
    return apiFetch<B2BInvitationResult>('/admin/kc/users/invite', {
      method: 'POST',
      data,
    });
  },

  getUser(userId: string) {
    return apiFetch<KeycloakUser & { groups: UserGroupMembership[] }>(
      `/admin/kc/users/${userId}`,
    );
  },

  updateUser(userId: string, data: UpdateUserPayload) {
    return apiFetch<KeycloakUser>(`/admin/kc/users/${userId}`, {
      method: 'PUT',
      data,
    });
  },

  deleteUser(userId: string) {
    return apiFetch<{ message: string }>(`/admin/kc/users/${userId}`, {
      method: 'DELETE',
    });
  },

  resetPassword(userId: string, password: string, temporary = true) {
    return apiFetch<{ message: string }>(`/admin/kc/users/${userId}/password`, {
      method: 'PUT',
      data: { password, temporary },
    });
  },

  listGroups() {
    return apiFetch<{ groups: KeycloakGroup[] }>('/admin/kc/groups');
  },

  createGroup(name: string, description?: string) {
    return apiFetch<KeycloakGroup>('/admin/kc/groups', {
      method: 'POST',
      data: { name, description },
    });
  },

  updateGroup(groupId: string, name: string, description?: string) {
    return apiFetch<KeycloakGroup>(`/admin/kc/groups/${groupId}`, {
      method: 'PUT',
      data: { name, description },
    });
  },

  getGroup(groupId: string) {
    return apiFetch<KeycloakGroup & { members: GroupMember[] }>(
      `/admin/kc/groups/${groupId}`,
    );
  },

  deleteGroup(groupId: string) {
    return apiFetch<{ message: string }>(`/admin/kc/groups/${groupId}`, {
      method: 'DELETE',
    });
  },

  getGroupMembers(groupId: string) {
    return apiFetch<{ members: GroupMember[] }>(
      `/admin/kc/groups/${groupId}/members`,
    );
  },

  addUserToGroup(groupId: string, userId: string) {
    return apiFetch<{ message: string }>(
      `/admin/kc/groups/${groupId}/members/${userId}`,
      {
        method: 'PUT',
      },
    );
  },

  removeUserFromGroup(groupId: string, userId: string) {
    return apiFetch<{ message: string }>(
      `/admin/kc/groups/${groupId}/members/${userId}`,
      { method: 'DELETE' },
    );
  },
};
